import { ArrowLeft, ExternalLink, FileText, Handshake, Package, PlayCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useParceirosControllerObter } from '@/api/generated/parceiros/parceiros';
import type {
  MaterialDoParceiroResponse,
  ProdutoDoParceiroResponse,
} from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { NotaEstrelas } from '@/features/dashboard/components/NotaEstrelas';
import { AvaliarMaterial } from '@/features/parceiros/components/AvaliarMaterial';
import { useTrackingParceiro } from '@/features/parceiros/hooks/useTrackingParceiro';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';

/**
 * Detalhe do parceiro: vídeos, materiais em PDF e produtos.
 *
 * ⚠️ Só materiais **APROVADOS** chegam aqui — o corte é do servidor, e não há
 * filtro de status nesta tela. Se um dia aparecer um pendente, o defeito é lá.
 */
export function ParceiroDetalhePage() {
  const { id = '' } = useParams<{ id: string }>();
  const { registrarClique } = useTrackingParceiro();

  const { data, isPending, isError, error, refetch } = useParceirosControllerObter(id);

  if (isPending) return <EstadoCarregando />;

  if (isError) {
    const naoEncontrado = error instanceof ApiError && error.status === 404;
    return naoEncontrado ? (
      <EstadoVazio
        icone={Handshake}
        titulo="Parceiro não encontrado"
        descricao="Ele pode ter saído da vitrine."
        acao={
          <Button asChild variant="outline">
            <Link to={ROTAS.parceiros}>Ver parceiros</Link>
          </Button>
        }
      />
    ) : (
      <EstadoErro aoTentarDeNovo={() => void refetch()} />
    );
  }

  const videos = data.materiais.filter((m) => m.tipo === 'VIDEO');
  const pdfs = data.materiais.filter((m) => m.tipo === 'PDF');

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground -ml-2">
        <Link to={ROTAS.parceiros}>
          <ArrowLeft className="w-4 h-4" />
          Voltar aos parceiros
        </Link>
      </Button>

      <Card className="border-border overflow-hidden">
        {/* Mesmo enquadramento da vitrine: sempre o logo, num quadro 3:1 —
            a mesma proporção que o servidor exige dele. Sem `max-h-*` aqui:
            junto de `aspect-[3/1]` ela força o navegador a recalcular a
            LARGURA a partir da altura travada, encolhendo o quadro pro canto
            esquerdo do card em vez de ocupar a largura inteira. `bg-card`, não
            branco fixo: quando o logo upado não preenche o quadro 3:1 exato,
            a sobra tem que se confundir com o fundo do card, não aparecer
            como uma borda branca. */}
        <div className="aspect-[3/1] bg-card flex items-center justify-center border-b border-border">
          {data.logoUrl ? (
            <img src={data.logoUrl} alt={data.nome} className="w-full h-full object-contain" />
          ) : (
            <Handshake className="w-16 h-16 text-muted-foreground/30" />
          )}
        </div>

        <CardContent className="p-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{data.nome}</h2>
            {data.categoria && (
              <Badge className="mt-2 bg-brand text-brand-foreground hover:bg-brand/80 text-xs">
                {data.categoria}
              </Badge>
            )}
            {data.descricao && (
              <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">
                {data.descricao}
              </p>
            )}
          </div>

          {data.link && (
            <a
              href={data.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => registrarClique(data.id)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand hover:bg-brand/80 text-brand-foreground text-sm font-medium transition-colors shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
              Visitar site
            </a>
          )}
        </CardContent>
      </Card>

      {videos.length > 0 && (
        <Secao titulo="Vídeos" icone={PlayCircle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((material) => (
              <CardDeVideo key={material.id} material={material} parceiroId={data.id} />
            ))}
          </div>
        </Secao>
      )}

      {pdfs.length > 0 && (
        <Secao titulo="Materiais" icone={FileText}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pdfs.map((material) => (
              <CardDePdf key={material.id} material={material} parceiroId={data.id} />
            ))}
          </div>
        </Secao>
      )}

      {data.materiais.length === 0 && (
        <EstadoVazio
          icone={FileText}
          titulo="Nada publicado ainda"
          descricao="Este parceiro ainda não teve materiais aprovados na plataforma."
        />
      )}

      {data.produtos.length > 0 && (
        <Secao titulo="Produtos" icone={Package}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.produtos.map((produto) => (
              <CardDeProduto key={produto.id} produto={produto} />
            ))}
          </div>
        </Secao>
      )}
    </div>
  );
}

function Secao({
  titulo,
  icone: Icone,
  children,
}: {
  titulo: string;
  icone: typeof FileText;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
        <Icone className="w-5 h-5 text-brand" />
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function CardDeVideo({
  material,
  parceiroId,
}: {
  material: MaterialDoParceiroResponse;
  parceiroId: string;
}) {
  return (
    <Card className="border-border overflow-hidden">
      {material.url && (
        <div className="aspect-video bg-black">
          {/* `material.url` é o embed RECONSTRUÍDO pelo servidor a partir do id
              do vídeo — nunca a URL que o parceiro digitou. É o que impede um
              `src` arbitrário dentro deste iframe. */}
          <iframe
            src={material.url}
            title={material.titulo}
            className="w-full h-full"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
      <CardContent className="p-4">
        <RodapeDoMaterial material={material} parceiroId={parceiroId} />
      </CardContent>
    </Card>
  );
}

function CardDePdf({
  material,
  parceiroId,
}: {
  material: MaterialDoParceiroResponse;
  parceiroId: string;
}) {
  return (
    <Card className="border-border">
      <CardContent className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <RodapeDoMaterial material={material} parceiroId={parceiroId} />
          {material.url && (
            <a
              href={material.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-brand mt-2 font-medium hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              Abrir PDF
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RodapeDoMaterial({
  material,
  parceiroId,
}: {
  material: MaterialDoParceiroResponse;
  parceiroId: string;
}) {
  return (
    <>
      <h4 className="font-semibold text-foreground">{material.titulo}</h4>
      {material.descricao && (
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{material.descricao}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <NotaEstrelas nota={material.notaMedia} total={material.totalAvaliacoes} />
        <AvaliarMaterial
          materialId={material.id}
          parceiroId={parceiroId}
          minhaNota={material.minhaNota}
        />
      </div>
    </>
  );
}

function CardDeProduto({ produto }: { produto: ProdutoDoParceiroResponse }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4 flex gap-3 items-start">
        {produto.imagemUrl ? (
          <img
            src={produto.imagemUrl}
            alt={produto.nome}
            className="w-16 h-16 rounded-lg object-contain border border-border bg-white shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
            <Package className="w-7 h-7 text-brand/50" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground text-sm">{produto.nome}</h4>
          {produto.categoria && <p className="text-xs text-brand mt-0.5">{produto.categoria}</p>}
          {produto.descricao && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{produto.descricao}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
